import {
  Button,
  Checkbox,
  createStyles,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import React, {useState} from 'react';

import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import StorableChip from 'inventory/chips/StorableChip';
import {GetScratchPlateCreatedPlates_scratchPlate_ScratchPlateNode_createdPlates as ScratchPlateCreatedPlate} from 'notebook/integrations/plate-maps/scratch-plates/__generated/GetScratchPlateCreatedPlates';
import {SCRATCH_PLATE_VIEWER_MIN_HEIGHT} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewConstants';
import ScratchPlateViewer from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewer';
import useFilledScratchPlateData from 'notebook/integrations/plate-maps/scratch-plates/useFilledScratchPlateData';
import {openPutScratchPlateInInventoryModal} from 'samples/plate-maps/scratch-plates/PutScratchPlateInInventoryModal';
import {CollapsibleSidebar} from 'samples/plate-maps/sidebar/CollapsibleSidebar';
import {PlateSize} from 'samples/storables/StorableSchemaConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {DATETIME_FORMATS, PYTHON_ISOFORMAT_WITH_TIMEZONE} from 'util/DateConstants';
import {formatDatetimeValue, isValidDateOrDatetimeOrTime} from 'util/DateUtil';

// `createdAt` comes from Python `datetime.isoformat()` (microsecond precision),
// which DATETIME_FORMATS doesn't cover, so add the microsecond format.
const CREATED_AT_FORMATS = [...DATETIME_FORMATS, PYTHON_ISOFORMAT_WITH_TIMEZONE];

export type RecordsListItem =
  | {
      kind: 'plate';
      id: string;
      name: string | null;
      createdAt: string | null;
      inventoryPlateId: string | null;
    }
  | {
      kind: 'inaccessible';
      id: string;
      invalidId: string;
    };

export function buildScratchPlateRecordsItems(
  createdPlates: Array<ScratchPlateCreatedPlate>
): Array<RecordsListItem> {
  // Backend returns created plates newest-first; preserve that order.
  return createdPlates.flatMap((createdPlate): Array<RecordsListItem> => {
    if (createdPlate.__typename === 'ScratchPlateNode') {
      return [
        {
          kind: 'plate',
          id: createdPlate.id,
          name: createdPlate.name,
          createdAt: createdPlate.createdAt,
          inventoryPlateId:
            createdPlate.inventoryPlate != null
              ? getAccessibleOrInaccessibleId(createdPlate.inventoryPlate)
              : null,
        },
      ];
    }
    if (isInaccessible(createdPlate)) {
      return [
        {
          kind: 'inaccessible',
          id: `inaccessible-${createdPlate.invalidId}`,
          invalidId: createdPlate.invalidId,
        },
      ];
    }
    return [];
  });
}

function formatCreatedAt(createdAt: string | null): string {
  if (createdAt == null) {
    return 'Created date unavailable';
  }

  if (!isValidDateOrDatetimeOrTime({textValue: createdAt, validFormats: CREATED_AT_FORMATS})) {
    return 'Created date unavailable';
  }

  const formattedCreatedAt = formatDatetimeValue(createdAt, CREATED_AT_FORMATS, 'lll');
  return `Created ${formattedCreatedAt}`;
}

function RecordsDetailView({
  scratchPlateId,
  scratchPlateName,
  inventoryPlateId,
  onPutInInventoryComplete,
}: {
  scratchPlateId: string;
  scratchPlateName: string | null;
  inventoryPlateId: string | null;
  onPutInInventoryComplete?: () => void;
}): JSX.Element {
  const {classes} = useStyles();
  const {result, plate} = useFilledScratchPlateData(scratchPlateId);
  const displayName = plate?.name ?? scratchPlateName ?? scratchPlateId;

  function handlePutInInventory(): void {
    if (plate == null) {
      return;
    }
    openPutScratchPlateInInventoryModal({
      plates: [
        {
          scratchPlateId,
          scratchPlateName: plate.name,
          plateSize: [plate.height, plate.width],
          plateSchemaId: plate.plateSchemaId,
        },
      ],
      onCompleted: onPutInInventoryComplete,
    });
  }

  return (
    <Flex direction="column" h="100%" w="100%">
      <Group position="apart" align="center" spacing={8}>
        <Text className={classes.recordsDetailTitle}>{`Viewing ${displayName}`}</Text>
        {inventoryPlateId != null ? (
          <Group spacing={6}>
            <StorableChip id={inventoryPlateId} size={ChipSize.SMALL} />
          </Group>
        ) : (
          <Button
            size="sm"
            leftIcon={<BenchlingIcon icon="box-arrow-right" />}
            variant="primary"
            disabled={plate == null}
            onClick={handlePutInInventory}
          >
            Put in inventory
          </Button>
        )}
      </Group>
      <Flex direction="column" mih={0} sx={{flex: 1}} w="100%">
        <div className={classes.plateViewerWrapper}>
          <ScratchPlateViewer result={result} />
        </div>
      </Flex>
    </Flex>
  );
}

interface ScratchPlateRecordsViewProps {
  recordsItems: Array<RecordsListItem>;
  selectedPlateId: string | null;
  onSelectPlateId: (id: string) => void;
  viewToggle?: React.ReactNode;
  /** When provided, renders a "Fill additional plates" button below the list. */
  onFillAdditionalPlates?: () => void;
  fillAdditionalPlatesDisabled?: boolean;
  /**
   * Dimensions and schema of the design these plates were filled from. All
   * created plates share them, so they seed the bulk put-in-inventory modal.
   */
  designPlateSize: PlateSize;
  designPlateSchemaId: string | null;
  onPutInInventoryComplete?: () => void;
}

export default function ScratchPlateRecordsView({
  recordsItems,
  selectedPlateId,
  onSelectPlateId,
  viewToggle,
  onFillAdditionalPlates,
  fillAdditionalPlatesDisabled = false,
  designPlateSize,
  designPlateSchemaId,
  onPutInInventoryComplete,
}: ScratchPlateRecordsViewProps): JSX.Element {
  const {classes, cx} = useStyles();
  const selectedPlateItem =
    selectedPlateId == null
      ? null
      : (recordsItems.find(
          (item): item is Extract<RecordsListItem, {kind: 'plate'}> =>
            item.kind === 'plate' && item.id === selectedPlateId
        ) ?? null);

  // Ids checked for the bulk put-in-inventory action. Kept separate from the
  // single-plate `selectedPlateId` (which drives the detail pane) so browsing
  // a plate's details doesn't change the bulk selection.
  const [checkedPlateIds, setCheckedPlateIds] = useState<ReadonlySet<string>>(new Set());

  const plateItems = recordsItems.filter(
    (item): item is Extract<RecordsListItem, {kind: 'plate'}> => item.kind === 'plate'
  );
  const checkedPlateItems = plateItems.filter((item) => checkedPlateIds.has(item.id));

  const allChecked = plateItems.length > 0 && checkedPlateItems.length === plateItems.length;
  const someChecked = checkedPlateItems.length > 0 && !allChecked;

  function toggleChecked(id: string): void {
    setCheckedPlateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleToggleSelectAll(): void {
    // If anything is selected, the header acts as "deselect all"; otherwise it
    // selects every plate.
    setCheckedPlateIds(checkedPlateItems.length > 0 ? new Set() : new Set(plateItems.map((item) => item.id)));
  }

  function handleBulkPutInInventory(): void {
    if (checkedPlateItems.length === 0) {
      return;
    }
    openPutScratchPlateInInventoryModal({
      plates: checkedPlateItems.map((item) => ({
        scratchPlateId: item.id,
        scratchPlateName: item.name,
        plateSize: designPlateSize,
        plateSchemaId: designPlateSchemaId,
      })),
      // Clear the selection once the request is accepted so the checkboxes
      // don't stay checked for plates that have been put in inventory.
      onSubmitted: () => {
        setCheckedPlateIds(new Set());
      },
      onCompleted: onPutInInventoryComplete,
    });
  }

  return (
    <Flex sx={{flex: 1}} miw={0}>
      <CollapsibleSidebar component="ScratchPlateRecordsSidebar">
        <Stack h="100%" spacing={8}>
          {viewToggle != null && <div className={classes.stickyToggleContainer}>{viewToggle}</div>}
          {plateItems.length > 0 && (
            <Group spacing="sm" noWrap={true} className={classes.selectAllHeader}>
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked}
                onChange={handleToggleSelectAll}
                aria-label={checkedPlateItems.length > 0 ? 'Deselect all plates' : 'Select all plates'}
              />
              <Text className={classes.selectAllLabel}>
                {checkedPlateItems.length > 0
                  ? `${checkedPlateItems.length} selected`
                  : `Select all (${plateItems.length})`}
              </Text>
            </Group>
          )}
          <Stack className={classes.recordsSidebarList} spacing={8}>
            {recordsItems.map((item) =>
              item.kind === 'plate' ? (
                <div
                  key={item.id}
                  className={cx(classes.recordsSidebarRow, {
                    [classes.recordsSidebarRowActive]: selectedPlateId === item.id,
                  })}
                >
                  <div className={classes.recordsSidebarRowControls}>
                    <Checkbox
                      checked={checkedPlateIds.has(item.id)}
                      onChange={() => {
                        toggleChecked(item.id);
                      }}
                      aria-label={`Select ${item.name ?? item.id} for put in inventory`}
                    />
                    <div className={classes.recordsSidebarRowContent}>
                      <UnstyledButton
                        onClick={() => {
                          onSelectPlateId(item.id);
                        }}
                        aria-pressed={selectedPlateId === item.id}
                        className={cx(classes.recordsSidebarSelectButton, {
                          [classes.recordsSidebarSelectButtonWithChip]: item.inventoryPlateId != null,
                        })}
                      >
                        <Stack spacing={2}>
                          <Text fw={600}>{item.name ?? item.id}</Text>
                          <Text className={classes.recordsSidebarRowMeta}>
                            {formatCreatedAt(item.createdAt)}
                          </Text>
                        </Stack>
                      </UnstyledButton>
                      {item.inventoryPlateId != null && (
                        <div className={classes.inventoryChipInline}>
                          <StorableChip id={item.inventoryPlateId} size={ChipSize.SMALL} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  key={item.id}
                  className={cx(classes.recordsSidebarRow, classes.recordsSidebarRowDisabled)}
                >
                  <Stack spacing={2}>
                    <Text fw={600}>Inaccessible plate</Text>
                    <Text className={classes.recordsSidebarRowMeta}>{item.invalidId}</Text>
                  </Stack>
                </div>
              )
            )}
          </Stack>
          {checkedPlateItems.length > 0 && (
            <div className={classes.fillButtonContainer}>
              <Button
                size="sm"
                leftIcon={<BenchlingIcon icon="box-arrow-right" />}
                variant="primary"
                fullWidth={true}
                onClick={handleBulkPutInInventory}
              >
                {`Put ${checkedPlateItems.length} in inventory`}
              </Button>
            </div>
          )}
          {onFillAdditionalPlates != null && (
            <div className={classes.fillButtonContainer}>
              <Button
                size="sm"
                leftIcon={<BenchlingIcon icon="fill-grid" />}
                variant="tertiary"
                fullWidth={true}
                disabled={fillAdditionalPlatesDisabled}
                onClick={onFillAdditionalPlates}
              >
                Fill additional plates
              </Button>
            </div>
          )}
        </Stack>
      </CollapsibleSidebar>
      <Divider orientation="vertical" className={classes.recordsDivider} />
      <Flex direction="column" sx={{flex: 1}} miw={0} className={classes.recordsPlatePane}>
        {selectedPlateId == null ? (
          // Keeps the empty state centered when the parent has no fixed height.
          <Flex justify="center" align="center" mih={SCRATCH_PLATE_VIEWER_MIN_HEIGHT} h="100%">
            <Text className={classes.emptyDetail}>Select a plate to view details.</Text>
          </Flex>
        ) : (
          <RecordsDetailView
            key={selectedPlateId}
            scratchPlateId={selectedPlateId}
            scratchPlateName={selectedPlateItem?.name ?? null}
            inventoryPlateId={selectedPlateItem?.inventoryPlateId ?? null}
            onPutInInventoryComplete={onPutInInventoryComplete}
          />
        )}
      </Flex>
    </Flex>
  );
}

const useStyles = createStyles((theme) => ({
  stickyToggleContainer: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: theme.white,
    paddingRight: theme.spacing.md,
    paddingBottom: theme.spacing.xs,
  },
  selectAllHeader: {
    paddingInline: theme.spacing.md,
    paddingBottom: theme.spacing.xs,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
  selectAllLabel: {
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
  },
  recordsSidebarList: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  fillButtonContainer: {
    borderTop: `1px solid ${theme.colors.gray[3]}`,
    paddingTop: theme.spacing.sm,
    paddingRight: theme.spacing.md,
  },
  recordsDivider: {
    margin: `-${theme.spacing.md} ${theme.spacing.md} -${theme.spacing.md} 0`,
    borderColor: theme.colors.gray[3],
  },
  recordsPlatePane: {
    overflow: 'auto',
  },
  recordsSidebarRow: {
    width: '100%',
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    background: theme.white,
    paddingBlock: theme.spacing.sm,
    paddingInline: theme.spacing.md,
  },
  recordsSidebarSelectButton: {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
  },
  recordsSidebarSelectButtonWithChip: {
    flex: '0 1 28%',
    minWidth: 88,
  },
  recordsSidebarRowControls: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  },
  recordsSidebarRowContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  recordsSidebarRowActive: {
    background: theme.colors.blue[0],
    borderColor: theme.colors.blue[4],
  },
  recordsSidebarRowDisabled: {
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  recordsSidebarRowMeta: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
  },
  inventoryChipInline: {
    flex: '1 1 72%',
    minWidth: 0,
    maxWidth: '72%',
    display: 'flex',
    justifyContent: 'flex-end',
    overflow: 'hidden',
    '& .chip': {
      maxWidth: '100%',
    },
  },
  recordsDetailTitle: {
    color: theme.colors.gray[7],
    fontWeight: 600,
  },
  emptyDetail: {
    color: theme.colors.gray[5],
  },
  plateViewerWrapper: {
    width: '100%',
    height: '100%',
    // PlateMapView sets minWidth inline on the .plate-map SVG, so !important is
    // needed to let the embedded viewer shrink to fit the detail pane.
    '.plate-map': {
      minWidth: '0 !important',
      width: '100%',
    },
    '.plate-map-legend': {
      width: '100%',
    },
  },
}));
